import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePagination } from "../../../../hooks/usePagination";
import api from "../../../../util/api";
import {
  ProductVariantApiType,
  PaginatedResponse,
  QueryKeys,
} from "../../../../util/types";

export interface ProductsFilters {
  from?: string;
  to?: string;
  searchKeyword?: string | null;
  createdBy?: string;
  currency?: string;
  companyId?: string;
}

export const useGetProducts = (
  filters: ProductsFilters = {},
  sort = { field: "createdAt", direction: "desc" },
  status: string | undefined,
  ownerId?: string
) => {
  const { page, limit, onPaginationChange, resetPageNum } = usePagination();

  const {
    data,
    isLoading,
    isFetching,
    refetch: refreshProducts,
  } = useQuery<PaginatedResponse<ProductVariantApiType>>({
    queryKey: [
      QueryKeys.LIST_PRODUCTS,
      page,
      limit,
      filters.searchKeyword ?? "",
      filters.from ?? "",
      filters.to ?? "",
      filters.createdBy ?? "",
      filters.currency ?? "",
      filters.companyId ?? "",
      sort.field,
      sort.direction,
      status ?? "",
      ownerId ?? "",
    ],
    queryFn: async () => {
      const { companyId, ...rest } = filters;
      return await api.getProducts(
        page,
        limit,
        "",
        {
          sort: sort.field,
          sortDirection: sort.direction,
          ...rest,
        },
        status,
        companyId ?? ownerId
      );
    },
    placeholderData: (prev) => prev,
  });

  useEffect(() => {
    resetPageNum();
  }, [JSON.stringify(filters), status, ownerId]);

  // Reset page if current page is beyond available pages after filtering
  useEffect(() => {
    if (data?.totalDocs !== undefined && page > 1) {
      const maxPage = Math.ceil(data.totalDocs / limit);
      if (maxPage > 0 && page > maxPage) {
        resetPageNum();
      }
    }
  }, [data?.totalDocs, page, limit, resetPageNum]);

  return {
    loading: isLoading || isFetching,
    refreshProducts,
    products: data?.docs ?? [],
    totalDocs: data?.totalDocs ?? 0,
    page,
    limit,
    onPaginationChange,
  };
};
